import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@skyment-org/shared-lib';
import { useAuth } from '../../contexts/Auth0Context';
import { useToast } from '../../contexts/ToastContext';
import RoleSelection from '../../components/onboarding/RoleSelection';
import MentorOnboardingForm, { MentorOnboardingData } from '../../components/onboarding/MentorOnboardingForm';
import MenteeOnboardingForm, { MenteeOnboardingData } from '../../components/onboarding/MenteeOnboardingForm';
import { auth0ApiClient } from '../../services/auth0ApiClient';
import { User } from '../../types/user';

type OnboardingStep = 'role-selection' | 'profile-completion';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { showToast } = useToast();

  const [step, setStep] = useState<OnboardingStep>('role-selection');
  const [selectedRole, setSelectedRole] = useState<UserRole.MENTOR | UserRole.MENTEE | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRoleSelect = async (role: UserRole.MENTOR | UserRole.MENTEE) => {
    setSelectedRole(role);
    setStep('profile-completion');
  };

  const handleBack = () => {
    setStep('role-selection');
    setSelectedRole(null);
  };

  const handleMentorSubmit = async (data: MentorOnboardingData) => {
    setIsLoading(true);
    try {
      // API returns updated user directly - use it to avoid race condition with refreshUser()
      const updatedUser = await auth0ApiClient.post<User>('/onboarding/mentor', data);
      updateUser(updatedUser);

      showToast({ type: 'success', message: 'Welcome! Your mentor profile is complete.' });

      // Navigate to mentor dashboard
      navigate('/mentor/dashboard');
    } catch (error) {
      console.error('Mentor onboarding error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete onboarding. Please try again.';
      showToast({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenteeSubmit = async (data: MenteeOnboardingData) => {
    setIsLoading(true);
    try {
      // API returns updated user directly - use it to avoid race condition with refreshUser()
      const updatedUser = await auth0ApiClient.post<User>('/onboarding/mentee', data);
      updateUser(updatedUser);

      showToast({ type: 'success', message: 'Welcome! Your mentee profile is complete.' });

      // Navigate to mentee dashboard
      navigate('/mentee/dashboard');
    } catch (error) {
      console.error('Mentee onboarding error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete onboarding. Please try again.';
      showToast({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {step === 'role-selection' && (
        <RoleSelection onRoleSelect={handleRoleSelect} />
      )}

      {step === 'profile-completion' && selectedRole === UserRole.MENTOR && (
        <MentorOnboardingForm
          onSubmit={handleMentorSubmit}
          onBack={handleBack}
          isLoading={isLoading}
        />
      )}

      {step === 'profile-completion' && selectedRole === UserRole.MENTEE && (
        <MenteeOnboardingForm
          onSubmit={handleMenteeSubmit}
          onBack={handleBack}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default OnboardingPage;
